import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useContext } from "react";
import { styled } from "@mui/system";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import deleteBankAccountInfo from "../../api/bank/delete_bank_account_info";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import ConfirmationDialog from "../VersatileComponents/ConfirmationDialog";
import BankAccountInformationEditForm from "./edit_bank_account_info";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "1.5rem",
  backgroundColor: theme.palette.background.alt,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  transition: "transform 0.2s ease-in-out",
  position: "relative",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
  },
}));

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography color="text.primary" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

const BankAccountInfoTable = ({ bank }) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { setIsSubmitting } = useContext(SpinnerContext);
  const { openSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);

  const handleDeleteIconClick = () => {
    handleMenuClose();
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleEdit = () => {
    handleMenuClose();
    setDialogOpen(true);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteConfirmed = async () => {
    handleDialogClose();
    setIsSubmitting(true);
    try {
      const res = await deleteBankAccountInfo(user, bank.id);
      openSnackbar(res.data.message, "success");
    } catch (error) {
      if (error.response && error.response.data) {
        openSnackbar(
          error.response.data.message,
          error.response.data.type ? error.response.data.type : "error"
        );
      } else {
        openSnackbar(
          "An unexpected error occurred.Please kindly check your connection.",
          "error"
        );
      }
    }
    setIsSubmitting(false);
    handleMenuClose();
  };

  return (
    <>
      <StyledCard>
        {/* More icon */}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteIconClick}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" /> Delete
          </MenuItem>
        </Menu>

        <CardContent sx={{ p: 5 }}>
          <InfoRow label="Bank Name" value={bank.bankName} />
          <Divider sx={{ my: 2 }} />
          <InfoRow label="Account Number" value={bank.accountNumber} />
        </CardContent>
        {dialogOpen && (
          <BankAccountInformationEditForm
            bankInfo={bank}
            isEditDialogOpen={dialogOpen}
            closeEditDialog={() => setDialogOpen(false)}
          />
        )}
        <ConfirmationDialog
          open={openDialog}
          handleDialogClose={handleDialogClose}
          handleConfirmed={handleDeleteConfirmed}
          message={`Are you sure you want to delete this Bank Account Information ?`}
          title={`Confirm Bank Account Information deletion`}
        />
      </StyledCard>
    </>
  );
};

export default BankAccountInfoTable;
